import * as React from 'react'

import { useNavigate } from '@tanstack/react-router'

import { useGetNotificationStats } from '@/api/v1/platform/notifications'
import { AffiliateActivationDialog } from '@/components/common/affiliate-activation-dialog'
import { AffiliateWelcomeDialog } from '@/components/common/affiliate-welcome-dialog'
import { FreePlanBannerWrapper } from '@/components/common/free-plan'
import { NoPlanBannerWrapper } from '@/components/common/no-plan'
import { ReactivateBannerWrapper } from '@/components/common/reactivate'
import { SandboxBannerWrapper } from '@/components/common/sandbox'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/sui/sidebar'
import { useAffiliateWelcomeStore } from '@/stores/affiliate-welcome'

import { AccountSwitcher } from '../account-switcher/account-switcher'
import { CreateAccountFlow } from '../create-account-flow'
import { NavMain } from '../nav-main'
import { NavSearch } from '../nav-search'
import { NavSecondary } from '../nav-secondary'
import { NavUser } from '../nav-user'
import { SearchDialog } from '../search-dialog'

import { useNotificationRealtime } from './hooks'

// The section's landing, same page `/affiliate` redirects to. Stated here
// rather than imported so activating the program does not pull the whole
// affiliate layout chunk into the sidebar.
const AFFILIATE_HOME_PATH = '/affiliate/overview'

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [createAccountOpen, setCreateAccountOpen] = React.useState(false)
  const [affiliateActivationOpen, setAffiliateActivationOpen] =
    React.useState(false)
  // Owned by the join mutation, not by this component: the section's own
  // landing is the other door into the program, and it must greet its new
  // affiliate too.
  const affiliateWelcomeOpen = useAffiliateWelcomeStore((state) => state.isOpen)
  const closeAffiliateWelcome = useAffiliateWelcomeStore(
    (state) => state.closeWelcome,
  )
  const navigate = useNavigate()
  const { data: notificationStats } = useGetNotificationStats()

  // SignalR listener: invalidates notification queries when new notifications arrive
  useNotificationRealtime()

  return (
    <>
      <Sidebar collapsible="icon" {...props}>
        <SidebarHeader>
          <AccountSwitcher onCreateAccount={() => setCreateAccountOpen(true)} />
          <NavSearch className="mt-1" onOpen={() => setSearchOpen(true)} />
        </SidebarHeader>
        <SidebarContent>
          <NavMain />
        </SidebarContent>
        <SidebarFooter>
          <SandboxBannerWrapper />
          <ReactivateBannerWrapper />
          <FreePlanBannerWrapper />
          <NoPlanBannerWrapper />
          <NavSecondary
            unreadNotifications={notificationStats?.nonRead ?? 0}
            onAffiliateActivationClick={() => setAffiliateActivationOpen(true)}
          />
          <NavUser />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <CreateAccountFlow
        open={createAccountOpen}
        onOpenChange={setCreateAccountOpen}
      />
      {/* Outside <Sidebar>, like the search dialog: on mobile the sidebar is a
          sheet that unmounts when it closes, and clicking a menu entry closes
          it. */}
      <AffiliateActivationDialog
        open={affiliateActivationOpen}
        onOpenChange={setAffiliateActivationOpen}
        onActivated={() => {
          setAffiliateActivationOpen(false)
          navigate({ to: AFFILIATE_HOME_PATH })
        }}
      />
      <AffiliateWelcomeDialog
        open={affiliateWelcomeOpen}
        onOpenChange={(open) => {
          if (!open) closeAffiliateWelcome()
        }}
      />
    </>
  )
}
